import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, EmptyState, Section } from "@redipos/ui";
import { Headphones, Loader2, MessageCircle, MessageCirclePlus, RefreshCw, Send } from "lucide-react";
import type { ConsoleTenantListItem, SupportMessageApi, SupportThreadRow } from "@redipos/contracts";
import { soporteApi } from "../api.js";
import StartSupportConversationModal from "../components/StartSupportConversationModal.js";
import {
  ADMIN_SUPPORT_ACTIVE_THREAD,
  ADMIN_SUPPORT_SSE_MESSAGE,
  ADMIN_SUPPORT_SSE_TYPING,
  ADMIN_SUPPORT_THREADS_SYNC,
} from "../lib/supportRealtimeEvents.js";

function errMsg(e: unknown, fallback: string) {
  return e instanceof Error ? e.message : fallback;
}

function formatTime(value: unknown) {
  if (!value) return "Sin actividad";
  return new Date(value as string | number | Date).toLocaleString("es-PY", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function byLastMessageDesc(a: SupportThreadRow, b: SupportThreadRow) {
  return new Date(b.lastMsgAt || 0).getTime() - new Date(a.lastMsgAt || 0).getTime();
}

export default function SupportPage() {
  const [threads, setThreads] = useState<SupportThreadRow[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [messages, setMessages] = useState<SupportMessageApi[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [clientTyping, setClientTyping] = useState(false);
  const [newConvoOpen, setNewConvoOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const typingDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedTenantIdRef = useRef(selectedTenantId);

  useEffect(() => {
    selectedTenantIdRef.current = selectedTenantId;
  }, [selectedTenantId]);

  const publishThreadsSync = (sorted: SupportThreadRow[]) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent(ADMIN_SUPPORT_THREADS_SYNC, { detail: { threads: sorted } }),
    );
  };

  // Escuchar el SSE centralizado del SupportBadgeProvider.
  useEffect(() => {
    const onMessage = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: SupportMessageApi }>).detail;
      const msg = detail?.message;
      if (!msg?.tenantId) return;

      loadThreads({ keepLoading: true }).catch(() => undefined);

      if (msg.tenantId === selectedTenantIdRef.current) {
        setMessages((prev) => {
          const idx = prev.findIndex((m) => m.id === msg.id);
          if (idx >= 0) {
            const next = prev.slice();
            next[idx] = { ...next[idx], ...msg };
            return next;
          }
          return [...prev, msg];
        });
      }
    };

    const onTyping = (event: Event) => {
      const typing = (event as CustomEvent<{ tenantId?: string }>).detail;
      if (typing?.tenantId && typing.tenantId === selectedTenantIdRef.current) {
        setClientTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setClientTyping(false), 4000);
      }
    };

    window.addEventListener(ADMIN_SUPPORT_SSE_MESSAGE, onMessage as EventListener);
    window.addEventListener(ADMIN_SUPPORT_SSE_TYPING, onTyping as EventListener);

    return () => {
      window.removeEventListener(ADMIN_SUPPORT_SSE_MESSAGE, onMessage as EventListener);
      window.removeEventListener(ADMIN_SUPPORT_SSE_TYPING, onTyping as EventListener);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    };
  }, []);

  const selectedThread = useMemo(
    () => threads.find((thread) => thread.id === selectedTenantId) ?? null,
    [selectedTenantId, threads],
  );

  const loadThreads = async ({ keepLoading = false } = {}) => {
    if (!keepLoading) setThreadsLoading(true);
    try {
      const data = await soporteApi.threads();
      const sorted = Array.isArray(data) ? [...data].sort(byLastMessageDesc) : [];
      setThreads(sorted);
      publishThreadsSync(sorted);
      setSelectedTenantId((current) => current || sorted[0]?.id || "");
      setError("");
    } catch (e) {
      setError(errMsg(e, "No se pudieron cargar las conversaciones."));
      setThreads([]);
    } finally {
      if (!keepLoading) setThreadsLoading(false);
    }
  };

  const loadMessages = async (tenantId: string, { keepLoading = false } = {}) => {
    if (!tenantId) {
      setMessages([]);
      return;
    }
    if (!keepLoading) setMessagesLoading(true);
    try {
      const data = await soporteApi.list(tenantId);
      setMessages(Array.isArray(data) ? data : []);
      await soporteApi.markRead(tenantId).catch(() => {});
      setThreads((prev) => {
        const next = prev.map((thread) =>
          thread.id === tenantId ? { ...thread, unread: 0 } : thread,
        );
        publishThreadsSync(next);
        return next;
      });
      setError("");
    } catch (e) {
      setError(errMsg(e, "No se pudieron cargar los mensajes."));
      setMessages([]);
    } finally {
      if (!keepLoading) setMessagesLoading(false);
    }
  };

  useEffect(() => {
    loadThreads();
    const id = window.setInterval(() => loadThreads({ keepLoading: true }), 12000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return undefined;
    loadMessages(selectedTenantId);
    const id = window.setInterval(
      () => loadMessages(selectedTenantId, { keepLoading: true }),
      4000,
    );
    return () => window.clearInterval(id);
  }, [selectedTenantId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Publicar el thread activo para que el SupportBadgeProvider (y cualquier
  // otro consumidor) sepa si el admin está mirando esta conversación
  // específica. Sin esto, el badge provider no puede decidir entre sonido
  // "new" y "add".
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent(ADMIN_SUPPORT_ACTIVE_THREAD, {
        detail: { tenantId: selectedTenantId || null },
      }),
    );
  }, [selectedTenantId]);

  const handleStartConversation = (tenant: ConsoleTenantListItem) => {
    const row: SupportThreadRow = {
      id: tenant.id,
      name: tenant.name,
      lastMsgAt: null,
      unread: 0,
    };
    setThreads((prev) => {
      const exists = prev.some((t) => t.id === tenant.id);
      const next = exists ? prev : [row, ...prev];
      publishThreadsSync(next);
      return next;
    });
    setSelectedTenantId(tenant.id);
    setMessages([]);
    setInput("");
    setNewConvoOpen(false);
    setError("");
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    const content = input.trim();
    if (!selectedTenantId || !content || sending) return;

    setSending(true);
    try {
      const created = await soporteApi.send(selectedTenantId, content);
      setMessages((prev) => [...prev, created]);
      setInput("");
      await loadThreads({ keepLoading: true });
    } catch (e) {
      setError(errMsg(e, "No se pudo enviar el mensaje."));
    } finally {
      setSending(false);
    }
  };

  // Notificar al cliente que el admin está escribiendo (debounced 1.5s).
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
    if (!selectedTenantId) return;
    if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    typingDebounceRef.current = setTimeout(() => {
      soporteApi.notifyTyping(selectedTenantId).catch(() => undefined);
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-500">
            Centro de ayuda
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-fg">
            Soporte por cliente
          </h1>
          <p className="mt-1 text-sm text-fg-subtle">
            Respondé conversaciones de clientes o iniciá un chat proactivo con cualquier tenant.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => setNewConvoOpen(true)}>
            <MessageCirclePlus className="h-4 w-4" />
            Nueva conversación
          </Button>
          <Button type="button" variant="secondary" onClick={() => loadThreads()}>
            <RefreshCw className="h-4 w-4" />
            Actualizar
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-input border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </div>
      ) : null}

      <div className="grid min-h-[620px] grid-cols-1 overflow-hidden rounded-card border border-border bg-surface shadow-card lg:grid-cols-[20rem_1fr]">
        <aside className="border-b border-border bg-surface-muted lg:border-b-0 lg:border-r">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Headphones className="h-4 w-4 text-brand-400" />
              <span className="text-sm font-semibold text-fg">Conversaciones</span>
            </div>
            <span className="rounded-full border border-border px-2 py-0.5 text-xs text-fg-subtle">
              {threads.length}
            </span>
          </div>

          <div className="max-h-[280px] overflow-y-auto p-2 lg:max-h-[calc(620px-49px)]">
            {threadsLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-fg-subtle">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando conversaciones...
              </div>
            ) : threads.length === 0 ? (
              <div className="space-y-3 p-4">
                <EmptyState message="Todavía no hay conversaciones de soporte." />
                <Button type="button" className="w-full" onClick={() => setNewConvoOpen(true)}>
                  <MessageCirclePlus className="h-4 w-4" />
                  Iniciar con un cliente
                </Button>
              </div>
            ) : (
              threads.map((thread, idx) => {
                const active = thread.id === selectedTenantId;
                return (
                  <button
                    key={thread.id ?? `thread-${idx}`}
                    type="button"
                    onClick={() => setSelectedTenantId(thread.id ?? "")}
                    className={`w-full rounded-input px-3 py-3 text-left transition-colors ${
                      active
                        ? "bg-surface-elevated text-fg shadow-card"
                        : "text-fg-muted hover:bg-surface-elevated/70 hover:text-fg"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">
                          {thread.name || "Tenant sin nombre"}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-fg-subtle">
                          {formatTime(thread.lastMsgAt)}
                        </p>
                      </div>
                      {thread.unread > 0 ? (
                        <span className="min-w-5 rounded-full bg-danger px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                          {thread.unread > 99 ? "99+" : thread.unread}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="flex min-h-[620px] flex-col bg-surface">
          <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-fg">
                {selectedThread?.name || "Seleccioná una conversación"}
              </p>
              {selectedThread ? (
                <p className="text-xs text-fg-subtle">Tenant: {selectedThread.id}</p>
              ) : null}
            </div>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
            {!selectedTenantId ? (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <EmptyState message="Elegí un cliente o iniciá una conversación nueva." />
                <Button type="button" onClick={() => setNewConvoOpen(true)}>
                  <MessageCirclePlus className="h-4 w-4" />
                  Nueva conversación
                </Button>
              </div>
            ) : messagesLoading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-subtle">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando mensajes...
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <EmptyState message="Esta conversación aún no tiene mensajes." />
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message) => {
                  const admin = Boolean(message.isAdmin);
                  return (
                    <div
                      key={message.id}
                      className={`flex ${admin ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm shadow-card ${
                          admin
                            ? "rounded-br-sm bg-brand-700 text-fg-on-brand"
                            : "rounded-bl-sm border border-border bg-surface-elevated text-fg"
                        }`}
                      >
                        <p className="whitespace-pre-line">{message.content}</p>
                        <p
                          className={`mt-1 text-[10px] font-medium ${
                            admin ? "text-brand-100" : "text-fg-subtle"
                          }`}
                        >
                          {message.sender?.usuario || (admin ? "Soporte" : "Cliente")}
                          {" · "}
                          {formatTime(message.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {clientTyping ? (
                  <div className="flex justify-start">
                    <div className="rounded-2xl rounded-bl-sm border border-border bg-surface-elevated px-3.5 py-2 text-xs italic text-fg-subtle">
                      El cliente está escribiendo
                      <span className="ml-1 inline-flex gap-0.5">
                        <span className="h-1 w-1 animate-bounce rounded-full bg-fg-subtle" style={{ animationDelay: "0ms" }} />
                        <span className="h-1 w-1 animate-bounce rounded-full bg-fg-subtle" style={{ animationDelay: "150ms" }} />
                        <span className="h-1 w-1 animate-bounce rounded-full bg-fg-subtle" style={{ animationDelay: "300ms" }} />
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <form onSubmit={handleSend} className="border-t border-border bg-surface-muted p-3">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={handleInputChange}
                disabled={!selectedTenantId || sending}
                placeholder={
                  selectedTenantId
                    ? messages.length === 0
                      ? "Primer mensaje al cliente..."
                      : "Escribí una respuesta para el cliente..."
                    : "Seleccioná una conversación..."
                }
                className="min-h-11 flex-1 rounded-input border border-border bg-surface-elevated px-3 text-sm text-fg placeholder:text-fg-subtle focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:opacity-60"
              />
              <Button type="submit" disabled={!selectedTenantId || !input.trim() || sending} loading={sending}>
                <Send className="h-4 w-4" />
                Enviar
              </Button>
            </div>
          </form>
        </section>
      </div>

      <StartSupportConversationModal
        open={newConvoOpen}
        onClose={() => setNewConvoOpen(false)}
        existingThreads={threads}
        onSelect={handleStartConversation}
      />
    </div>
  );
}
